import React, { useRef, useEffect, useCallback } from "react";
import { Container } from "@pixi/react";
import { playSound } from "../../../State/Slices/soundsSlice";
import { SOUND_NAME } from "../../../sounds";
import { setGameType } from "../../../State/Slices/gameSettingsSlice";
import { useSelector, useDispatch } from "react-redux";
import { setLinesRotationFinished } from "../../../State/Slices/spinSlice";
import FreeSpinReel from "../FreeSpinReel/FreeSpinReel";
import FreeSpinReelSetController from "./FreeSpinReelSetController";
import FreeSpinReelsetBackground from "../FreeSpinReelSetBackground/FreeSpinReelSetBackground";

const FreeSpinReelSet = ({ dimensions, symbolSize, freeSpinReelRefs }) => {
  const freeSpinReelSetController = useRef(null);
  const dispatch = useDispatch();

  const { spinComplete, isSpinning } = useSelector((state) => state.spin);
  const { currentRound, winningLines } = useSelector((state) => state.game);

  const previousReels = currentRound?.reels.map((reel) => {
    return [...reel, ...reel];
  });

  // ✅ Replace `round[roundIndex]?.type` with `currentRound?.type`
  useEffect(() => {
    dispatch(setGameType(currentRound?.type));
  }, [currentRound, dispatch]);

  const handleSetLinesRotation = useCallback(
    (linesRotationFinished) => {
      dispatch(setLinesRotationFinished(linesRotationFinished));
    },
    [dispatch]
  );

  const handleSetCurrentWinningLine = useCallback(
    (currentWinningLine) => {
      if (currentWinningLine) {
        dispatch(playSound(SOUND_NAME[`winningLine${currentWinningLine}`]));
      }
    },
    [dispatch]
  );

  // ✅ Optimize effect to check `currentRound?.wins` instead of `round[roundIndex]?.wins`
  useEffect(() => {
    if (
      freeSpinReelSetController.current &&
      spinComplete &&
      Object.keys(currentRound?.wins || {}).length > 0
    ) {
      const winningData =
        freeSpinReelSetController.current?.matchHorizontalWinningLines(
          winningLines,
          currentRound?.wins
        );
      freeSpinReelSetController.current?.startWinningLinesAnimation(
        winningData
      );
    }
  }, [spinComplete, winningLines, currentRound]);

  useEffect(() => {
    if (isSpinning && !spinComplete) {
      freeSpinReelSetController.current?.stopWinningLinesAnimation();
    }
  }, [isSpinning, spinComplete]);

  useEffect(() => {
    if (freeSpinReelRefs.current && !freeSpinReelSetController.current) {
      freeSpinReelSetController.current = new FreeSpinReelSetController(
        freeSpinReelRefs.current,
        handleSetLinesRotation,
        handleSetCurrentWinningLine
      );
      freeSpinReelRefs.current.forEach((reel, index) => {
        freeSpinReelSetController.current.reelControllers[index] =
          reel.getReelController();
      });
    }
  }, [freeSpinReelRefs, handleSetLinesRotation, handleSetCurrentWinningLine]);

  return (
    <FreeSpinReelsetBackground dimensions={dimensions} symbolSize={symbolSize}>
      {previousReels.map((reel, index) => (
        <Container
          key={index}
          name={"FreeSpinReelContainer"}
          x={symbolSize * (index - 2)}
          y={-symbolSize}
          zIndex={3}
          sortableChildren
        >
          <FreeSpinReel
            handleSetCurrentWinningLine={handleSetCurrentWinningLine}
            ref={(el) => {
              freeSpinReelRefs.current[index] = el;
            }}
            reel={reel}
            symbolSize={symbolSize}
            index={index}
          />
        </Container>
      ))}
    </FreeSpinReelsetBackground>
  );
};

export default FreeSpinReelSet;
